import React, { useEffect, useState, useContext, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Unstable_Grid2";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CSDataTable from "../../components/CSDataTable";
import { CSDataTableColumns } from "../../modal/CSDataTable";
import { convertToDateMonthFormat } from "../../utils/ConvertDate";
import { DisplayResponsePlan } from "./components/DisplayResponsePlan";
import DialogBoxComponent from "../../components/DialogBoxComponent";
import {
  createIncidentResponsePlan,
  fetchIncidentResponsePlanList,
  fetchIncidentResponsePlanTemplate,
  updateIncidentResponsePlan,
} from "./helper";
import { useUserContext } from "../../context/auth/UserContext";
import { useIncidentResponsePlanContext } from "../../context/IncidentResponsePlan/IncidentPlanResponceContext";
import { Box, CircularProgress } from "@mui/material";
import ImportDialogBox from "../../components/ImportDialogBox";
import DuplicateDialogBox from "../../components/DuplicateDialogBox";
import { useAccessRights } from "../../utils/useAccessRights";

const columns: CSDataTableColumns[] = [
  {
    accessorKey: "version",
    header: "Version",
    size: "60px",
  },
  {
    accessorKey: "status",
    header: "Status",
    size: "80px",
  },
  {
    accessorKey: "createdDate",
    header: "Created Date",
    size: "80px",
  },
  {
    accessorKey: "approvedDate",
    header: "Approved Date",
    size: "80px",
  },
];

export const IncidentResponsePlan = () => {
  const navigate = useNavigate();
  const incidentResponsePlanAccess = useAccessRights("Incident Response Plan");

  const [responsePlanList, setResponsePlanList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [latestVersion, setLatestVersion] = useState<any>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogImportState, setDialogImportState] = useState({
    title: "",
    body: "",
    isBtnsDisabled: false,
    openImportDialog: false,
  });
  const [dialogDuplicateState, setDialogDuplicateState] = useState({
    title: "",
    body: "",
    isBtnsDisabled: false,
    openDuplicateDialog: false,
  });
  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openDuplicateDialog: false,
    openSuccessDialogBox: false,
  });

  const { userId, companyProfileID } = useUserContext();
  const { setSelectedIncidentResponsePlan } = useIncidentResponsePlanContext();

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectRow = (selectedRows: any) => {
    setSelectedIncidentResponsePlan(selectedRows);
  };

  const displayAddPolicy = () => {
    setSelectedIncidentResponsePlan([]);
    navigate("/incident-response-plan-form");
  };

  const displayIncidentPlaybook = () => {
    navigate("/incident-response-playbook");
  };

  const displayIncidentTest = () => {
    navigate("/incident-response-test");
  };

  const handleDialogCancel = () => {
    if (dialogState.title === "Success") {
      fetchTableData();
    }
    setDialogState({
      ...dialogState,
      openDuplicateDialog: false,
      openSuccessDialogBox: false,
    });
    setDialogImportState({
      ...dialogState,
      isBtnsDisabled: false,
      openImportDialog: false,
    });
    setDialogDuplicateState({
      ...dialogState,
      isBtnsDisabled: false,
      openDuplicateDialog: false,
    });
  };

  const displayImportPlan = () => {
    setDialogImportState({
      title: "Import Incident Response Plan",
      body: `By importing the template plan, you will overwrite the content in any existing version 1.0 plan for your organization.`,
      isBtnsDisabled: false,
      openImportDialog: true,
    });
  };

  const handleImportPlan = async () => {
    setIsLoading(true);
    setDialogImportState({
      ...dialogImportState,
      isBtnsDisabled: true,
    });
    try {
      const importTemplates = await fetchIncidentResponsePlanTemplate(
        userId,
        companyProfileID
      );
      const isExsistingPlan = responsePlanList.filter(
        (ele: any) => ele.version === importTemplates[0].version
      );

      let hasError = false;
      const date = new Date();

      if (isExsistingPlan.length > 0) {
        const payload = {
          overview: importTemplates[0].overview,
          purpose: importTemplates[0].purpose,
          scope: importTemplates[0].scope,
          definition: importTemplates[0].definitions,
          compliance: importTemplates[0].compliance,
          incidentResponseTeam: importTemplates[0].incidentResponseTeam,
          preparation: importTemplates[0].preparation,
          detection: importTemplates[0].detection,
          analysis: importTemplates[0].analysis,
          containment: importTemplates[0].containment,
          eradication: importTemplates[0].eradication,
          recovery: importTemplates[0].recovery,
          reporting: importTemplates[0].reporting,
          relatedPolicies: importTemplates[0].relatedPolicies,
          version: importTemplates[0].version,
          dateApproved: null,
          status: "Not Approved",
          updateDate: date,
          updateBy: userId,
          companyProfileID: companyProfileID,
          approvedBy: 0,
          incidentResponseInfoPlanID:
            isExsistingPlan[0].incidentResponseInfoPlanID,
        };

        const resUpdate = await updateIncidentResponsePlan(payload);
        // console.log("resUpdate",resUpdate)
        if (!resUpdate.succeeded) {
          hasError = true;
        }
      } else {
        const payload = {
          overview: importTemplates[0].overview,
          purpose: importTemplates[0].purpose,
          scope: importTemplates[0].scope,
          definition: importTemplates[0].definitions,
          compliance: importTemplates[0].compliance,
          incidentResponseTeam: importTemplates[0].incidentResponseTeam,
          preparation: importTemplates[0].preparation,
          detection: importTemplates[0].detection,
          analysis: importTemplates[0].analysis,
          containment: importTemplates[0].containment,
          eradication: importTemplates[0].eradication,
          recovery: importTemplates[0].recovery,
          reporting: importTemplates[0].reporting,
          relatedPolicies: importTemplates[0].relatedPolicies,
          version: importTemplates[0].version,
          dateApproved: null,
          status: "Not Approved",
          companyProfileID: companyProfileID,
          createDate: date,
          createdBy: userId,
          approvedBy: 0,
        };
        const resImport = await createIncidentResponsePlan(payload);
        // console.log("resImport",resImport)
        if (!resImport.succeeded) {
          hasError = true;
        }
      }

      if (hasError) {
        setDialogState({
          title: "Error",
          body: "Unexpected Error",
          action: "OK",
          actionCancel: "",
          openDuplicateDialog: false,
          openSuccessDialogBox: true,
        });
      } else {
        setSelectedIncidentResponsePlan([]);
        setDialogState({
          title: "Success",
          body: "Plan successfully imported",
          action: "OK",
          actionCancel: "",
          openDuplicateDialog: false,
          openSuccessDialogBox: true,
        });
      }
    } finally {
      setIsLoading(false);
      setDialogImportState({
        ...dialogImportState,
        isBtnsDisabled: false,
      });
    }
  };

  const displayDuplicateConfirm = () => {
    handleClose();
    const _latestVersion = responsePlanList.sort(
      (a: any, b: any) => parseFloat(b.version) - parseFloat(a.version)
    )[0];
    // console.log("latestVersion", _latestVersion)
    setLatestVersion(_latestVersion);
    setDialogDuplicateState({
      title: `Duplicate Incident Response Plan`,
      body: `Are you sure you want to duplicate lastest version <strong>${_latestVersion.version}</strong> of Incident Response Plan?`,
      openDuplicateDialog: true,
      isBtnsDisabled: false,
    });
  };

  const duplicatePlan = async () => {
    setDialogDuplicateState({
      ...dialogDuplicateState,
      isBtnsDisabled: true,
    });
    try {
      const date = new Date();
      const payload = {
        overview: latestVersion.overview,
        purpose: latestVersion.purpose,
        scope: latestVersion.scope,
        definition: latestVersion.definition,
        compliance: latestVersion.compliance,
        incidentResponseTeam: latestVersion.incidentResponseTeam,
        preparation: latestVersion.preparation,
        detection: latestVersion.detection,
        analysis: latestVersion.analysis,
        containment: latestVersion.containment,
        eradication: latestVersion.eradication,
        recovery: latestVersion.recovery,
        reporting: latestVersion.reporting,
        relatedPolicies: latestVersion.relatedPolicies,
        version: latestVersion.version,
        dateApproved: null,
        status: "Not Approved",
        createDate: date,
        createdBy: userId,
        companyProfileID: companyProfileID,
        approvedBy: 0,
      };
      const res = await createIncidentResponsePlan(payload);
      if (res.succeeded) {
        handleDialogCancel();
        fetchTableData();
        setDialogState({
          title: "Success",
          body: "Plan successfully added",
          action: "OK",
          actionCancel: "",
          openDuplicateDialog: false,
          openSuccessDialogBox: true,
        });
      } else {
        setDialogState({
          title: "Error",
          body: "Unexpected Error",
          action: "OK",
          actionCancel: "",
          openDuplicateDialog: false,
          openSuccessDialogBox: true,
        });
      }
    } finally {
      setDialogDuplicateState({
        ...dialogDuplicateState,
        isBtnsDisabled: false,
      });
    }
  };

  const fetchTableData = async () => {
    setIsLoading(true);
    try {
      const res = await fetchIncidentResponsePlanList(companyProfileID);
      const list = res.map((ele: any) => ({
        ...ele,
        rowId: ele.incidentResponseInfoPlanID,
        approvedDate: convertToDateMonthFormat(ele.dateApproved),
        createdDate: convertToDateMonthFormat(ele.createDate),
      }));

      setResponsePlanList(list);
    } catch (error) {
      // console.log("@@Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTableData();
  }, []);

  return (
    <Fragment>
      <Grid container sx={{ height: "100%", width: "100%" }}>
        <Grid
          xs={6}
          style={{
            padding: "1rem",
            margin: "1rem 0",
            background: "#FFF",
            borderRadius: "2%",
          }}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid xs={3}>
              <h3 style={{ fontWeight: "400" }}>
                Incident Response Plan ({responsePlanList.length})
              </h3>
            </Grid>
            <Grid>
              <Button
                size="small"
                variant="contained"
                className="table-btn"
                onClick={displayAddPolicy}
                disabled={!incidentResponsePlanAccess?.isCreate || isLoading}
              >
                Create New Plan
              </Button>
              <Button
                size="small"
                variant="contained"
                className="table-btn"
                onClick={displayImportPlan}
                disabled={!incidentResponsePlanAccess?.isImport || isLoading}
              >
                Import
              </Button>
              <IconButton
                aria-label="menu"
                disabled={isLoading}
                onClick={handleClick}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem
                  disabled={!incidentResponsePlanAccess?.isCreate}
                  onClick={displayDuplicateConfirm}
                >
                  Duplicate Plan
                </MenuItem>
                <MenuItem onClick={displayIncidentPlaybook}>PlayBook</MenuItem>
                <MenuItem onClick={displayIncidentTest}>Tests</MenuItem>
              </Menu>
            </Grid>
          </Grid>
          {isLoading ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <CSDataTable
              rows={responsePlanList}
              columns={columns}
              multiRowSelection={false}
              previewRecord={responsePlanList[0]}
              handleSelectRow={(selectedRows) => handleSelectRow(selectedRows)}
            />
          )}
        </Grid>

        <Grid
          xs
          style={{
            padding: "1rem",
            margin: "1rem",
            background: "#FFF",
            borderRadius: "2%",
          }}
        >
          <DisplayResponsePlan refreshList={() => fetchTableData()} />
        </Grid>
      </Grid>
      {
        <ImportDialogBox
          openDialogBox={dialogImportState.openImportDialog}
          dialogTitle={dialogImportState.title}
          dialogBody={dialogImportState.body}
          isBtnsDisabled={dialogImportState.isBtnsDisabled}
          handleFunction={handleImportPlan}
          handleCancel={handleDialogCancel}
        />
      }
      {
        <DuplicateDialogBox
          openDialogBox={dialogDuplicateState.openDuplicateDialog}
          dialogTitle={dialogDuplicateState.title}
          dialogBody={dialogDuplicateState.body}
          isBtnsDisabled={dialogDuplicateState.isBtnsDisabled}
          handleFunction={duplicatePlan}
          handleCancel={handleDialogCancel}
        />
      }
      {
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleDialogCancel}
        />
      }
    </Fragment>
  );
};
